import { useCallback } from "react";
import { DateTime } from "luxon";
import {
  getAllEnabledSettings,
  getAllDisabledSettings,
  uiSettingsToAPIFormat,
} from "../../../shared/utils/route-notification-utils";
import useRoutePlannerApi from "../../../hooks/use-route-planner-api";

interface UseRoutePlannerNotificationsApiProps {
  selectedDate: DateTime;
  pendingNotificationUpdate:
    | {
        routeId: string;
        routeName: string;
      }
    | null
    | undefined;
  closeModal: () => void;
}

export const useRoutePlannerNotificationsApi = ({
  selectedDate,
  pendingNotificationUpdate,
  closeModal,
}: UseRoutePlannerNotificationsApiProps) => {
  // Get notification API functions directly from the main API hook
  const { updateGlobalNotifications, updateRouteNotifications } =
    useRoutePlannerApi({
      date: selectedDate.toFormat("MM-dd-yyyy"),
    });
  const handleMuteAllNotificationsRoute = useCallback(async () => {
    if (!pendingNotificationUpdate) {
      closeModal();
      return;
    }

    try {
      await updateRouteNotifications({
        pathParams: { routeId: pendingNotificationUpdate.routeId },
        body: getAllDisabledSettings(),
      });
    } catch (error) {
      console.error("Failed to mute route notifications:", error);
    }
    closeModal();
  }, [pendingNotificationUpdate, updateRouteNotifications, closeModal]);

  const handleEnableAllNotificationsRoute = useCallback(async () => {
    if (!pendingNotificationUpdate) {
      closeModal();
      return;
    }

    try {
      await updateRouteNotifications({
        pathParams: { routeId: pendingNotificationUpdate.routeId },
        body: getAllEnabledSettings(),
      });
    } catch (error) {
      console.error("Failed to enable route notifications:", error);
    }
    closeModal();
  }, [pendingNotificationUpdate, updateRouteNotifications, closeModal]);

  const handleMuteAllNotificationsGlobal = useCallback(async () => {
    try {
      await updateGlobalNotifications({
        pathParams: { date: selectedDate.toFormat("yyyy-MM-dd") },
        body: getAllDisabledSettings(),
      });
    } catch (error) {
      console.error("Failed to mute all notifications:", error);
    }
    closeModal();
  }, [selectedDate, updateGlobalNotifications, closeModal]);

  const handleEnableAllNotificationsGlobal = useCallback(async () => {
    try {
      await updateGlobalNotifications({
        pathParams: { date: selectedDate.toFormat("yyyy-MM-dd") },
        body: getAllEnabledSettings(),
      });
    } catch (error) {
      console.error("Failed to enable all notifications:", error);
    }
    closeModal();
  }, [selectedDate, updateGlobalNotifications, closeModal]);

  const handleNotificationSettingsSave = useCallback(
    async (settings: Record<string, boolean>) => {
      try {
        if (pendingNotificationUpdate) {
          // Route-specific save
          await updateRouteNotifications({
            pathParams: { routeId: pendingNotificationUpdate.routeId },
            body: uiSettingsToAPIFormat(settings),
          });
        }
      } catch (error) {
        console.error("Failed to save notification settings:", error);
      }
      closeModal();
    },
    [
      pendingNotificationUpdate,
      selectedDate,
      updateRouteNotifications,
      updateGlobalNotifications,
      closeModal,
    ],
  );

  const handleGlobalNotificationSettingsSave = useCallback(
    async (settings: Record<string, boolean>) => {
      try {
        await updateGlobalNotifications({
          pathParams: { date: selectedDate.toFormat("yyyy-MM-dd") },
          body: uiSettingsToAPIFormat(settings),
        });
      } catch (error) {
        console.error("Failed to save notification settings:", error);
      }
      closeModal();
    },
    [
      selectedDate,
      updateRouteNotifications,
      updateGlobalNotifications,
      closeModal,
    ],
  );

  return {
    handleMuteAllNotificationsRoute,
    handleEnableAllNotificationsRoute,
    handleMuteAllNotificationsGlobal,
    handleEnableAllNotificationsGlobal,
    handleNotificationSettingsSave,
    handleGlobalNotificationSettingsSave,
  };
};
